import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const STICK_THRESHOLD_PX = 32;

interface UseAutoScrollResult {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  isPinned: boolean;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

/**
 * Keeps a chat log pinned to its newest message while the user is at the
 * bottom. `enabled: false` (e.g. the welcome screen, which reads top-down)
 * leaves the scroll position alone.
 */
export const useAutoScroll = (
  deps: ReadonlyArray<unknown>,
  { enabled = true }: { enabled?: boolean } = {},
): UseAutoScrollResult => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isPinned, setIsPinned] = useState(true);
  // For the resize observer below, which outlives any one render.
  const followRef = useRef(enabled && isPinned);
  useLayoutEffect(() => {
    followRef.current = enabled && isPinned;
  }, [enabled, isPinned]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      setIsPinned(distance < STICK_THRESHOLD_PX);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // The log getting shorter (a form opening below it, the composer growing)
  // would push the newest lines out of view — stay on them while pinned.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (followRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (enabled && isPinned) scrollToBottom("smooth");
  }, [...deps, enabled]);

  return { scrollRef, isPinned, scrollToBottom };
};
